import {PagedData, PagedDataItem} from '@app/views/databrowsing/model/paged-data';
import {DistrictEntity, Guid, IEntity, ILightEntity} from '@app/views/databrowsing/model/common.data.models';

/**
 * An assessment lineItem is a collection of results for a set of students who are completing an assignment.
 */
export class AssessmentLineItemModel extends DistrictEntity implements IEntity {
    schoolId: Guid;
    title: string;
    description: string;

    classId: Guid; // Roster GUID
    parentAssessmentLineItemId: Guid; // AssessmentLineItem GUID
    scoreScaleId: Guid; // ScoreScale GUID

    resultValueMin: string;
    resultValueMax: string;

    learningObjectiveSet: LearningObjectiveSet[];

    /*
    * links (linkedEntities?):
    * - School
    * - Roster
    * - AssessmentLineItem
    * - ScoreScale
    * */
}

export class AssessmentLineItemLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    localId = '';
    schoolId = '';
    schoolName = '';
    districtName = '';

    title = '';

    classId = '';
    className = '';
    parentAssessmentLineItemId = '';
    parentAssessmentLineItemName = '';
    scoreScaleId = '';
    scoreScaleName = '';

    createdAt = '';
    lastUpdated = '';

    constructor(data?: PagedDataItem<AssessmentLineItemModel>) {
        if (!data) {
            return;
        }
        const assessmentLineItem = data.data;
        this.guid = assessmentLineItem.guid ? assessmentLineItem.guid.guidValue : '';
        this.localId = assessmentLineItem.localId;
        this.schoolId = assessmentLineItem.schoolId ? assessmentLineItem.schoolId.guidValue : '';
        this.schoolName = data.linkedEntities &&
            data.linkedEntities['school'] && (data.linkedEntities['school'].length > 0) && data.linkedEntities['school'][0].name
            ? data.linkedEntities['school'][0].name
            : this.schoolId;

        this.title = assessmentLineItem.title;

        this.classId = assessmentLineItem.classId ? assessmentLineItem.classId.guidValue : '';
        this.className = data.linkedEntities &&
            data.linkedEntities['roster'] && (data.linkedEntities['roster'].length > 0) && data.linkedEntities['roster'][0].name
            ? data.linkedEntities['roster'][0].name
            : this.classId;
        this.parentAssessmentLineItemId = assessmentLineItem.parentAssessmentLineItemId ? assessmentLineItem.parentAssessmentLineItemId.guidValue : '';
        this.parentAssessmentLineItemName = data.linkedEntities &&
            data.linkedEntities['parentAssessmentLineItem'] && (data.linkedEntities['parentAssessmentLineItem'].length > 0) && data.linkedEntities['parentAssessmentLineItem'][0].name
            ? data.linkedEntities['parentAssessmentLineItem'][0].name
            : this.parentAssessmentLineItemId;
        this.scoreScaleId = assessmentLineItem.scoreScaleId ? assessmentLineItem.scoreScaleId.guidValue : '';
        this.scoreScaleName = data.linkedEntities &&
            data.linkedEntities['scoreScale'] && (data.linkedEntities['scoreScale'].length > 0) && data.linkedEntities['scoreScale'][0].name
            ? data.linkedEntities['scoreScale'][0].name
            : this.scoreScaleId;

        this.createdAt = assessmentLineItem.createdAt;
        this.lastUpdated = assessmentLineItem.lastUpdatedAt;

        if (assessmentLineItem.districtId && (this.schoolId === assessmentLineItem.districtId.guidValue)) {
            this.districtName = this.schoolName;
            this.schoolName = this.schoolId;

            if (!this.districtName || this.districtName === this.schoolName) {
                this.districtName = data.linkedEntities &&
                    data.linkedEntities['district'] && (data.linkedEntities['district'].length > 0) && data.linkedEntities['district'][0].name
                    ? data.linkedEntities['district'][0].name
                    : this.schoolName;
            }
        }
    }
}

/**
 * An assessment result consists of the assigned score plus the context for that score.
 */
export class AssessmentResultModel extends DistrictEntity implements IEntity {
    schoolId: Guid;

    assessmentLineItemId: Guid; // AssessmentLineItem GUID
    studentId: Guid; // Student GUID

    score: string;
    textScore: string;
    scoreDate: string; // Date
    scoreScaleId: Guid; // ScoreScale GUID
    scorePercentile: string;
    scoreStatus: ScoreStatus;
    comment: string;

    learningObjectiveSet: LearningObjectiveSet[];

    /*
    * links (linkedEntities?):
    * - School
    * - AssessmentLineItem
    * - ScoreScale
    * - Student
    * */
}

export class AssessmentResultLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    localId = '';
    schoolId = '';
    schoolName = '';
    districtName = '';

    score = '';
    scoreStatus = '';

    assessmentLineItemId = '';
    assessmentLineItemName = '';
    scoreScaleId = '';
    scoreScaleName = '';
    studentId = '';
    studentName = '';

    createdAt = '';
    lastUpdated = '';

    constructor(data?: PagedDataItem<AssessmentResultModel>) {
        if (!data) {
            return;
        }
        const assessmentResult = data.data;
        this.guid = assessmentResult.guid ? assessmentResult.guid.guidValue : '';
        this.localId = assessmentResult.localId;
        this.schoolId = assessmentResult.schoolId ? assessmentResult.schoolId.guidValue : '';
        this.schoolName = data.linkedEntities &&
            data.linkedEntities['school'] && (data.linkedEntities['school'].length > 0) && data.linkedEntities['school'][0].name
            ? data.linkedEntities['school'][0].name
            : this.schoolId;

        this.score = (assessmentResult.score || assessmentResult.textScore || '');
        this.scoreStatus = <string>assessmentResult.scoreStatus;

        this.assessmentLineItemId = assessmentResult.assessmentLineItemId ? assessmentResult.assessmentLineItemId.guidValue : '';
        this.assessmentLineItemName = data.linkedEntities &&
            data.linkedEntities['assessmentLineItem'] && (data.linkedEntities['assessmentLineItem'].length > 0) && data.linkedEntities['assessmentLineItem'][0].name
            ? data.linkedEntities['assessmentLineItem'][0].name
            : this.assessmentLineItemId;
        this.scoreScaleId = assessmentResult.scoreScaleId ? assessmentResult.scoreScaleId.guidValue : '';
        this.scoreScaleName = data.linkedEntities &&
            data.linkedEntities['scoreScale'] && (data.linkedEntities['scoreScale'].length > 0) && data.linkedEntities['scoreScale'][0].name
            ? data.linkedEntities['scoreScale'][0].name
            : this.scoreScaleId;
        this.studentId = assessmentResult.studentId ? assessmentResult.studentId.guidValue : '';
        this.studentName = data.linkedEntities &&
            data.linkedEntities['student'] && (data.linkedEntities['student'].length > 0) && data.linkedEntities['student'][0].name
            ? data.linkedEntities['student'][0].name
            : this.studentId;

        this.createdAt = assessmentResult.createdAt;
        this.lastUpdated = assessmentResult.lastUpdatedAt;

        if (assessmentResult.districtId && (this.schoolId === assessmentResult.districtId.guidValue)) {
            this.districtName = this.schoolName;
            this.schoolName = this.schoolId;

            if (!this.districtName || this.districtName === this.schoolName) {
                this.districtName = data.linkedEntities &&
                    data.linkedEntities['district'] && (data.linkedEntities['district'].length > 0) && data.linkedEntities['district'][0].name
                    ? data.linkedEntities['district'][0].name
                    : this.schoolName;
            }
        }
    }
}

export class CategoryModel extends DistrictEntity implements IEntity {
    schoolId: Guid;
    title: string;
    weight: string;

    /*
    * links (linkedEntities?):
    * - School
    * */
}

export class CategoryLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    localId = '';
    schoolId = '';
    schoolName = '';
    districtName = '';

    title = '';

    createdAt = '';
    lastUpdated = '';

    constructor(data?: PagedDataItem<CategoryModel>) {
        if (!data) {
            return;
        }
        const category = data.data;
        this.guid = category.guid ? category.guid.guidValue : '';
        this.localId = category.localId;
        this.schoolId = category.schoolId ? category.schoolId.guidValue : '';
        this.schoolName = data.linkedEntities &&
            data.linkedEntities['school'] && (data.linkedEntities['school'].length > 0) && data.linkedEntities['school'][0].name
            ? data.linkedEntities['school'][0].name
            : this.schoolId;

        this.title = category.title;

        this.createdAt = category.createdAt;
        this.lastUpdated = category.lastUpdatedAt;

        if (category.districtId && (this.schoolId === category.districtId.guidValue)) {
            this.districtName = this.schoolName;
            this.schoolName = this.schoolId;

            if (!this.districtName || this.districtName === this.schoolName) {
                this.districtName = data.linkedEntities &&
                    data.linkedEntities['district'] && (data.linkedEntities['district'].length > 0) && data.linkedEntities['district'][0].name
                    ? data.linkedEntities['district'][0].name
                    : this.schoolName;
            }
        }
    }
}

/*
 *   This is the container for the definition of a lineItem.
 *   A lineItem is a collection of results for a set of students
 *   who are completing an assignment as part of a class at a school.
 *   https://www.imsglobal.org/sites/default/files/spec/oneroster/v1p2/gradebook-informationmodel/OneRosterv1p2GradebookService_InfoModelv1p0.html#Data_LineItem
 */
export class LineItemModel extends DistrictEntity implements IEntity {
    schoolId: Guid;
    assignDate: string;
    dueDate: string;
    title: string;
    description: string;

    category: Guid; // Category GUID
    classId: Guid; // Roster GUID
    gradingPeriod: Guid; // Calendar GUID

    resultValueMin: string;
    resultValueMax: string;

    resourceId: string;
    tag: string;
    resourceLinkId: string;

    academicSession: Guid; // Calendar GUID
    scoreScaleId: Guid; // ScoreScale GUID
    learningObjectiveSet: LearningObjectiveSet[];

    results: PagedData<ResultModel>;

    /*
    * links (linkedEntities?):
    * - School
    * - Category
    * - Roster
    * - Calendar gradingPeriod
    * - Calendar academicSession
    * - ScoreScale
    * */
}

export class LineItemLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    localId = '';
    schoolId = '';
    schoolName = '';
    districtName = '';

    assignDate = '';
    dueDate = '';
    title = '';

    category = '';
    categoryName = '';
    classId = '';
    className = '';
    gradingPeriod = '';
    gradingPeriodName = '';
    academicSessionId = '';
    academicSessionName = '';
    scoreScaleId = '';
    scoreScaleName = '';

    createdAt = '';
    lastUpdated = '';

    constructor(data?: PagedDataItem<LineItemModel>) {
        if (!data) {
            return;
        }
        const result = data.data;
        this.guid = result.guid ? result.guid.guidValue : '';
        this.localId = result.localId;
        this.schoolId = result.schoolId ? result.schoolId.guidValue : '';
        this.schoolName = data.linkedEntities &&
            data.linkedEntities['school'] && (data.linkedEntities['school'].length > 0) && data.linkedEntities['school'][0].name
            ? data.linkedEntities['school'][0].name
            : this.schoolId;

        this.assignDate = result.assignDate;
        this.dueDate = result.dueDate;
        this.title = result.title;

        this.category = result.category ? result.category.guidValue : '';
        this.categoryName = data.linkedEntities &&
            data.linkedEntities['category'] && (data.linkedEntities['category'].length > 0) && data.linkedEntities['category'][0].name
            ? data.linkedEntities['category'][0].name
            : this.category;
        this.classId = result.classId ? result.classId.guidValue : '';
        this.className = data.linkedEntities &&
            data.linkedEntities['roster'] && (data.linkedEntities['roster'].length > 0) && data.linkedEntities['roster'][0].name
            ? data.linkedEntities['roster'][0].name
            : this.classId;
        this.gradingPeriod = result.gradingPeriod ? result.gradingPeriod.guidValue : '';
        this.gradingPeriodName = data.linkedEntities &&
            data.linkedEntities['gradingPeriod'] && (data.linkedEntities['gradingPeriod'].length > 0) && data.linkedEntities['gradingPeriod'][0].name
            ? data.linkedEntities['gradingPeriod'][0].name
            : this.gradingPeriod;
        this.academicSessionId = result.academicSession ? result.academicSession.guidValue : '';
        this.academicSessionName = data.linkedEntities &&
            data.linkedEntities['academicSession'] && (data.linkedEntities['academicSession'].length > 0) && data.linkedEntities['academicSession'][0].name
            ? data.linkedEntities['academicSession'][0].name
            : this.academicSessionId;
        this.scoreScaleId = result.scoreScaleId ? result.scoreScaleId.guidValue : '';
        this.scoreScaleName = data.linkedEntities &&
            data.linkedEntities['scoreScale'] && (data.linkedEntities['scoreScale'].length > 0) && data.linkedEntities['scoreScale'][0].name
            ? data.linkedEntities['scoreScale'][0].name
            : this.scoreScaleId;

        this.createdAt = result.createdAt;
        this.lastUpdated = result.lastUpdatedAt;

        if (result.districtId && (this.schoolId === result.districtId.guidValue)) {
            this.districtName = this.schoolName;
            this.schoolName = this.schoolId;

            if (!this.districtName || this.districtName === this.schoolName) {
                this.districtName = data.linkedEntities &&
                    data.linkedEntities['district'] && (data.linkedEntities['district'].length > 0) && data.linkedEntities['district'][0].name
                    ? data.linkedEntities['district'][0].name
                    : this.schoolName;
            }
        }
    }
}

export class ResultModel extends DistrictEntity implements IEntity {
    schoolId: Guid;
    lineItem: Guid; // LineItem GUID
    student: Guid; // Student GUID
    score: string;
    scoreMaximum: string;
    scoreStatus: string;
    activityProgress: ActivityProgress;
    scoreDate: string;
    comment: string;
    textScore: string;

    classId: Guid; // Roster GUID
    scoreScaleId: Guid; // ScoreScale GUID

    learningObjectiveSet: LearningObjectiveSet[];

    /*
    * links (linkedEntities?):
    * - School
    * - LineItem
    * - Student
    * - Roster
    * - ScoreScale
    * */
}

export class ResultLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    localId = '';
    schoolId = '';
    schoolName = '';
    districtName = '';

    score = '';
    scoreStatus = '';
    activityProgress = '';

    lineItem = '';
    lineItemId = '';
    lineItemName = '';
    student = '';
    studentId = '';
    studentName = '';
    classId = '';
    className = '';
    scoreScaleId = '';
    scoreScaleName = '';

    createdAt = '';
    lastUpdated = '';

    constructor(data?: PagedDataItem<ResultModel>) {
        if (!data) {
            return;
        }
        const result = data.data;
        this.guid = result.guid ? result.guid.guidValue : '';
        this.localId = result.localId;
        this.schoolId = result.schoolId ? result.schoolId.guidValue : '';
        this.schoolName = data.linkedEntities &&
            data.linkedEntities['school'] && (data.linkedEntities['school'].length > 0) && data.linkedEntities['school'][0].name
            ? data.linkedEntities['school'][0].name
            : this.schoolId;

        this.score = (result.score || result.textScore || '');
        this.score = this.score + (this.score && result.scoreMaximum ? ` of ${result.scoreMaximum}` : '');
        this.scoreStatus = result.scoreStatus;
        this.activityProgress = <string>result.activityProgress;

        this.lineItem = result.lineItem ? result.lineItem.guidValue : '';
        this.lineItemId = result.lineItem ? result.lineItem.guidValue : '';
        this.lineItemName = data.linkedEntities &&
            data.linkedEntities['lineItem'] && (data.linkedEntities['lineItem'].length > 0) && data.linkedEntities['lineItem'][0].name
            ? data.linkedEntities['lineItem'][0].name
            : this.lineItemId;
        this.student = result.student ? result.student.guidValue : '';
        this.studentId = result.student ? result.student.guidValue : '';
        this.studentName = data.linkedEntities &&
            data.linkedEntities['student'] && (data.linkedEntities['student'].length > 0) && data.linkedEntities['student'][0].name
            ? data.linkedEntities['student'][0].name
            : this.studentId;
        this.classId = result.classId ? result.classId.guidValue : '';
        this.className = data.linkedEntities &&
            data.linkedEntities['roster'] && (data.linkedEntities['roster'].length > 0) && data.linkedEntities['roster'][0].name
            ? data.linkedEntities['roster'][0].name
            : this.classId;
        this.scoreScaleId = result.scoreScaleId ? result.scoreScaleId.guidValue : '';
        this.scoreScaleName = data.linkedEntities &&
            data.linkedEntities['scoreScale'] && (data.linkedEntities['scoreScale'].length > 0) && data.linkedEntities['scoreScale'][0].name
            ? data.linkedEntities['scoreScale'][0].name
            : this.scoreScaleId;

        this.createdAt = result.createdAt;
        this.lastUpdated = result.lastUpdatedAt;

        if (result.districtId && (this.schoolId === result.districtId.guidValue)) {
            this.districtName = this.schoolName;
            this.schoolName = this.schoolId;

            if (!this.districtName || this.districtName === this.schoolName) {
                this.districtName = data.linkedEntities &&
                    data.linkedEntities['district'] && (data.linkedEntities['district'].length > 0) && data.linkedEntities['district'][0].name
                    ? data.linkedEntities['district'][0].name
                    : this.schoolName;
            }
        }
    }
}

export class ScoreScaleModel extends DistrictEntity implements IEntity {
    schoolId: Guid;
    title: string;
    scoreScaleType: string;

    courseId: Guid; // Course GUID
    classId: Guid; // Roster GUID

    scoreScaleValue: ScoreScaleValue[];

    /*
    * links (linkedEntities?):
    * - School
    * - Course
    * - Roster
    * */
}

export class ScoreScaleLightModel implements ILightEntity {
    guid = '';
    idValue = '';
    localId = '';
    schoolId = '';
    schoolName = '';
    districtName = '';

    title = '';
    scoreScaleType = '';

    courseId = '';
    courseName = '';
    classId = '';
    className = '';

    'scoreScaleValue.length' = '';
    scoreScaleValues = '';

    createdAt = '';
    lastUpdated = '';

    constructor(data?: PagedDataItem<ScoreScaleModel>) {
        if (!data) {
            return;
        }
        const scoreScale = data.data;
        this.guid = scoreScale.guid ? scoreScale.guid.guidValue : '';
        this.localId = scoreScale.localId;
        this.schoolId = scoreScale.schoolId ? scoreScale.schoolId.guidValue : '';
        this.schoolName = data.linkedEntities &&
            data.linkedEntities['school'] && (data.linkedEntities['school'].length > 0) && data.linkedEntities['school'][0].name
            ? data.linkedEntities['school'][0].name
            : this.schoolId;

        this.title = scoreScale.title;
        this.scoreScaleType = scoreScale.scoreScaleType;

        this['scoreScaleValue.length'] = scoreScale.scoreScaleValue ? String(scoreScale.scoreScaleValue.length) : '0';
        this.scoreScaleValues = scoreScale.scoreScaleValue ? scoreScale.scoreScaleValue.map((item: ScoreScaleValue) => item.toString()).join(',') : '';

        this.courseId = scoreScale.courseId ? scoreScale.courseId.guidValue : '';
        this.courseName = data.linkedEntities &&
            data.linkedEntities['roster'] && (data.linkedEntities['roster'].length > 0) && data.linkedEntities['roster'][0].name
            ? data.linkedEntities['roster'][0].name
            : this.courseId;
        this.classId = scoreScale.classId ? scoreScale.classId.guidValue : '';
        this.className = data.linkedEntities &&
            data.linkedEntities['roster'] && (data.linkedEntities['roster'].length > 0) && data.linkedEntities['roster'][0].name
            ? data.linkedEntities['roster'][0].name
            : this.classId;

        this.createdAt = scoreScale.createdAt;
        this.lastUpdated = scoreScale.lastUpdatedAt;

        if (scoreScale.districtId && (this.schoolId === scoreScale.districtId.guidValue)) {
            this.districtName = this.schoolName;
            this.schoolName = this.schoolId;

            if (!this.districtName || this.districtName === this.schoolName) {
                this.districtName = data.linkedEntities &&
                    data.linkedEntities['district'] && (data.linkedEntities['district'].length > 0) && data.linkedEntities['district'][0].name
                    ? data.linkedEntities['district'][0].name
                    : this.schoolName;
            }
        }
    }
}

/*
 * This is the container for the set of learning objective identifiers
 * that are from a single source responsible for creating those identifiers.
*/
export class LearningObjectiveSet {
    source: Source;
    learningObjectiveIds: string[];

    static isEmpty(learningObjectiveSet: LearningObjectiveSet): boolean {
        return !learningObjectiveSet || (!learningObjectiveSet.source
            && (!learningObjectiveSet.learningObjectiveIds || learningObjectiveSet.learningObjectiveIds.length === 0
                || learningObjectiveSet.learningObjectiveIds.every((item: string) => !item)));
    }
}

export class ScoreScaleValue {
    itemValueLHS: string;
    itemValueRHS: string;

    toString(): string {
        return `${this.itemValueLHS} - ${this.itemValueRHS}`;
    }
}

/*
 *   The source responsible for creating the learning objective identifiers.
 *   The permitted values are from an extensible vocabulary.
 *   Used in LearninObjectiveScoreSet
 */
export enum Source {
    PATTERN = 'PATTERN', // pattern
    CASE = 'CASE', // case
    UNKNOWN = 'UNKNOWN' // unknown
}

/*
 *   This is an extensible vocabulary data-type for
 *   the status of the score being submitted.
 *   Used in Result
 */
export enum ScoreStatus {
    FULLY_GRADED = 'FULLY_GRADED', // FullyGraded
    PENDING = 'PENDING', // Pending
    FAILED = 'FAILED', // Failed
    NOT_READY = 'NOT_READY', // NotReady
    PATTERN = 'PATTERN', // Pattern
    EXEMPT = 'EXEMPT', // Exempt
    NOT_SUBMITTED = 'NOT_SUBMITTED', // NotSubmitted
    PARTIALLY_GRADED = 'PARTIALLY_GRADED', // PartiallyGraded
    SUBMITTED = 'SUBMITTED', // Submitted
    LATE = 'LATE', // LATE
    INCOMPLETE = 'INCOMPLETE', // Incomplete
    MISSING = 'MISSING', // Missing
    WITHDRAWAL = 'WITHDRAWAL', // Withdrawal
    IN_PROGRESS = 'IN_PROGRESS' // InProgress
}

export enum ActivityProgress {
    INITIALIZED = 'INITIALIZED', // Initialized
    STARTED = 'STARTED', // Started
    IN_PROGRESS = 'IN_PROGRESS', // InProgress
    SUBMITTED = 'SUBMITTED', // Submitted
    COMPLETED = 'COMPLETED' // Completed
}
